import { AfterViewInit, Component, OnDestroy, TemplateRef, ViewChild, inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { Router } from '@angular/router';
import { MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { Subscription, combineLatest, filter, take } from 'rxjs';
import { HttpRequestResponse } from '../../../lib/models/http-request-response.model';
import { DialogMaterialService } from '../../../lib/services/dialog-material.service';
import { HttpRequestService } from '../../../lib/services/http-request.service';
import { UtenteService } from '../../../lib/services/utente.service';

interface News {
  id: string | number;
  data_inserimento: string;
  testo: string;
}

@Component({
  selector: 'app-news-accesso',
  template: `
    <ng-template #messaggio>
      <h2 mat-dialog-title>Novità del {{ newsCorrente?.data_inserimento }}</h2>
      <mat-dialog-content><div [innerHTML]="newsCorrente?.testo"></div></mat-dialog-content>
      <mat-dialog-actions align="end">
        <button mat-button type="button" (click)="apriNovita()">Vai alle Novità</button>
        <button mat-button type="button" (click)="chiudi(true)">Mostra al prossimo accesso</button>
        <button mat-raised-button color="primary" type="button" (click)="chiudi(false)">Non mostrare più</button>
      </mat-dialog-actions>
    </ng-template>
  `,
  imports: [MatButton, MatDialogTitle, MatDialogContent, MatDialogActions]
})
export class NewsAccessoComponent implements AfterViewInit, OnDestroy {
  private utente = inject(UtenteService);
  private http = inject(HttpRequestService);
  private dialog = inject(DialogMaterialService);
  private router = inject(Router);
  private subscriptions = new Subscription();
  private dialogRef: MatDialogRef<any>;
  private paziente: string;

  @ViewChild('messaggio') messaggio: TemplateRef<any>;
  newsCorrente: News;

  ngAfterViewInit(): void {
    const pazientePronto$ = this.utente.datiUtentePresenti.pipe(
      filter(presenti => presenti && !!this.utente.getChiave(['paziente', 'codfis'])),
      take(1)
    );

    // Il componente viene creato a ogni accesso e a ogni caricamento della pagina.
    // Richiediamo subito la news, anche se i dati del paziente sono ancora in arrivo.
    this.subscriptions.add(combineLatest([
      pazientePronto$,
      this.http.get('/news', { last: true, aggiornamento: Date.now() }, {}, false, false)
    ]).pipe(take(1)).subscribe({
      next: ([, response]) => {
        this.paziente = this.utente.getChiave(['paziente', 'codfis']);
        if (HttpRequestResponse.isSuccessResponse(response) && Array.isArray(response.data)) {
          const ultimaNews = (response.data as News[])[0];
          if (ultimaNews?.id != null && !this.nascosta(ultimaNews)) {
            this.newsCorrente = ultimaNews;
            this.dialogRef = this.dialog.open(this.messaggio, { disableClose: true, panelClass: 'dialog-large' });
          }
        }
      },
      error: () => { /* La richiesta sarà ripetuta al prossimo accesso. */ }
    }));
  }

  private chiave(news: News): string {
    return `sct-news-nascosta-${encodeURIComponent(this.paziente)}-${encodeURIComponent(String(news.id))}`;
  }

  private nascosta(news: News): boolean {
    try {
      return localStorage.getItem(this.chiave(news)) === 'S';
    } catch {
      return false;
    }
  }

  apriNovita(): void {
    this.dialogRef?.close();
    this.router.navigate(['/news']);
  }

  chiudi(rivedi: boolean): void {
    if (!rivedi) {
      try {
        localStorage.setItem(this.chiave(this.newsCorrente), 'S');
      } catch {
        // Se la memoria non è disponibile, il messaggio sarà riproposto.
      }
    }
    this.dialogRef?.close();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.dialogRef?.close();
  }
}
